import useConfigureRevenueCat from '@/src/modules/pricing/hooks/useConfigureRevenueCat';
import { useCurrentDeviceStore } from '@/src/modules/pricing/hooks/useCurrentDeviceStore';
import { useIsRevenueCatAvailable } from '@/src/modules/pricing/hooks/useIsRevenueCatAvailable';
import { pricingQueryKeys } from '@/src/modules/pricing/queries/pricingQueryKeys';
import { AnalyticsEvents } from '@fabric-app/business/analytics/analytics.types';
import { useAnalytics } from '@fabric-app/business/analytics/hooks/useAnalytics';
import { PurchaseError, PurchaseErrorCode } from '@fabric-app/business/pricing/pricing.types';
import { toast } from '@fabric-app/business/store/alerts';
import { userQueryKeys } from '@fabric-app/business/user/queries/userQueryKeys';
import { useOptimisticallyUpdateQueriesWithPredicate } from '@fabric-app/queries/utilities';
import { ensureError } from '@fabric-app/utils/guards';
import type { CustomerInfo } from '@revenuecat/purchases-capacitor';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { captureException } from '@sentry/nextjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mutate } from 'swr';

export const useMutationRevenueCatRestorePurchase = () => {
  const configureRevenueCat = useConfigureRevenueCat();
  const isRevenueCatAvailable = useIsRevenueCatAvailable();
  const optimisticallyUpdateQueries = useOptimisticallyUpdateQueriesWithPredicate();
  const currentDeviceStore = useCurrentDeviceStore();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  const mutation = useMutation({
    mutationFn: async (): Promise<CustomerInfo> => {
      track(AnalyticsEvents.ClickedRestorePurchase, {
        store: currentDeviceStore,
      });

      if (!isRevenueCatAvailable) {
        throw new PurchaseError(PurchaseErrorCode.GENERIC, currentDeviceStore);
      }

      await configureRevenueCat();

      return (await Purchases.restorePurchases()).customerInfo;
    },
    onSuccess: (result) => {
      optimisticallyUpdateQueries<CustomerInfo>(
        (q) => q.queryKey[0] === pricingQueryKeys.revenueCatCustomerInfo()[0],
        () => result,
      );

      toast({
        content:
          'Restore was successful. If there where any active subscriptions for this Fabric account, they have been restored.',
      });

      /**
       * unfortunately server returns old value, probably takes time to process (webhooks and what not)
       * so we need to wait a bit before revalidating the woody-user
       */
      setTimeout(() => {
        mutate('woody-user');
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.userInfo(),
        });
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.quota(),
        });
      }, 1000);
    },

    onError: (error) => {
      toast({
        content:
          'There was an error while trying to restore your purchase. Please try again later.',
      });
      captureException(ensureError(error));
    },
  });

  return mutation;
};
